import React, {createRef, useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import parse from "html-react-parser";
import MaterialTable, {MTableToolbar} from "@material-table/core";
import {
  TablePagination,
  Grid,
  makeStyles,
  Link,
  Tooltip,
  Button,
  Icon,
  useTheme,
  Badge,
  Typography, Switch
} from "@material-ui/core";
import {
  DoneAll,
  FlightTakeoff,
  Highlight,
  History,
  PlaylistAddCheck,
  SaveAlt,
  Email, LocalGasStation, SettingsEthernet, MonetizationOn, GridOn
} from "@material-ui/icons";
import {MoreTime, Preview, RestartAlt} from "@mui/icons-material";
import IconButtonMenu from "../../../controls/IconButtonMenu";
import {ColumnWidth, ValidatorColumn, dateSearch} from "../../../blocks/ColumnHelper";

import SystemContext from "../../../SystemContext";
import {
  useTrips,
  useAddTrip,
  useUpdateTrip,
  useUpdateTripCosts,
} from "../../../services/trips";

import TripLegList from "./TripLegList";
import TripClients from "./TripClients";
import TripMessaging from "./TripMessaging";
import TripsValidator from "../validators/TripsValidator";
import TripCosts from "./TripCosts";
import TripConfirmation from "./TripConfirmation";
import AirportSelectionDialog from "./AirportSelectionDialog";
import CustomerSelectionDialog from './CustomerSelectionDialog';
import dialogs, {EditTripDialog} from "./TripListDialogs";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import BuildPrimaryRouteFromLegs from '../utils/BuildPrimaryRouteFromLegs';
import BuildOnwardRouteIconFromLegs from '../utils/BuildOnwardRouteIconFromLegs';
import BuildTruckRouteIconFromLegs from '../utils/BuildTruckRouteIconFromLegs';
import BuildFlightListFromLegs from '../utils/BuildFlightListFromLegs';
import Icons, {
  AirCargoIcon,
  AirCargoInverseIcon,
  FlightScheduleIcon,
  UldContainerClosedIcon,
  UldContainerIcon
} from '../../../icons';
import TripUldStacks from './TripUldStacks';
import useTripDepartmentUsers from "../../../services/auth/useTripDepartmentUsers";
import { getCostingByUcn, getCostingDefaultValues } from '../../Costings/includes/costingDialogFunctions';
import CostingsStyles from '../../Costings/styles/CostingsStyles';
import ViewCostingDialog from "../../Costings/components/ViewCostingDialog";
import EditCostingDialog from '../../Costings/components/EditCostingDialog';
import { useUpdateCosting } from '../../../services/costings';
import ContextMenu from '../../../controls/ContextMenu';
import DocumentViewer from "../../Documents/DocumentViewer";
import { ComboSelect, MaskedNumber } from '../../../controls';
import TripReportCriteriaDialog from './TripListDialogs/TripReportCriteriaDialog';
import FinanceTripLegsDialog from './TripListDialogs/FinanceTripLegsDialog';
import ViewFuelAnnexDialog from './TripListDialogs/ViewFuelAnnexDialog';
import TripContractInvoiceDialog from './TripListDialogs/TripContractInvoiceDialog';
import {Stack} from "@mui/material";

const AccessControl = require("../../../AccessControl");
const apiSitePrefix = getApiSitePrefix();

const renderUserName = (user) => {
  const userInfo = AccessControl.getUserInfo();
  if (userInfo && "firstName" in userInfo && "lastName" in userInfo)
    return (userInfo.firstName + " " + userInfo.lastName);
  else
    return user.name;
}

const newTripRecordTemplate = {
  recordType: "TRIP",
  aircraft: [],
  legs:[],
  cancelled: false,
  tripNo: null,
}

const emptyLeg = {
  airlineCode: null,
  flightNo: null,
  originAirportCode: null,
  destinationAirportCode: null,
}

const Checked = (props) => {
  const state = (props.checked === true)?'check_box':'check_box_outline_blank';
  return <Icon {...props}>{state}</Icon>
}

const buildPrimaryRouteFromLegs = (legs) => {
  const primaryLegs = __.filter(legs, {transportType: 'PRIMARY'});
  const airports = [];
  if (primaryLegs.length > 0) {
    airports.push(primaryLegs[0].originAirportCode);
    primaryLegs.forEach(leg => {
      airports.push(leg.destinationAirportCode);
    });
  }
  return (airports.join("-"));
}

const missingActualCosting = (tripRoute, actualCosts) => {
  const legs = tripRoute.split("-");
  if (!actualCosts) {
    return [`${legs.length}`, "No actual costs captured for this trip."];
  }

  const actualCostsJson = JSON.parse(actualCosts);
  const {mappings, route} = actualCostsJson;

  if (!__.has(actualCostsJson, 'mappings'))
    return [`${legs.length}`, "No actual costs captured for this trip."];

  const summary = [];

  route.forEach(leg => {
    summary.push(
      {
        actualItems: 0,
        budgetItems: 0
      }
    );
  });

  let actualItems = 0;
  let budgetItems = 0;
  for (const mapping of mappings) {
    const actualRouteTotals = mapping.actualRouteTotals;
    if (actualRouteTotals && Array.isArray(actualRouteTotals)) {
      for (let i = 0; i < summary.length; i++) {
        summary[i].actualItems += actualRouteTotals[i] ? 1 : 0;
        summary[i].budgetItems += 1;
        actualItems += actualRouteTotals[i] ? 1 : 0;
        budgetItems += 1;
      }
    }
  }


  let legsMissingActuals = 0
  summary.forEach((leg, index) => {
    if (leg.actualItems === 0) {
      legsMissingActuals += 1;
    }
  });

  const tooltipMessage = legsMissingActuals === summary.length ? "No actual costs captured for this trip." : `${legsMissingActuals} of ${summary.length} legs missing actual costs. ${actualItems} actual cost items captured of total ${budgetItems}`;
  return [`${legsMissingActuals}`, tooltipMessage];
}

const TripList = (_props) => {
  const {isAuthenticated, user} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }
  const classes = props.classes;
  const costingClasses = makeStyles(CostingsStyles)();

  const operatorName = __.startCase(__.toLower(process.env.REACT_APP_COMPANY)) || "Magma";

  const tableRef = createRef({state: {lastEditingRow: false}});
  const month = props.month;
  const year = props.year;

  const queryClient = useQueryClient();

  let extraKeys = [year, month];
  if (props.showReview) {
    if (props.financeView) {
      extraKeys = [`FinanceReview${props.hideFutureReviewItems?"HideFuture":""}`];
    } else {
      extraKeys = [`Review${props.hideFutureReviewItems?"HideFuture":""}`];
    }
  }

  const handleAddTrip = useAddTrip();
  const handleUpdateTrip = useUpdateTrip();
  const handleUpdateTripCosts = useUpdateTripCosts();
  const handleUpdateCosting = useUpdateCosting();

  const trips = useTrips({tableRef, queryClient, extraKeys, params: props.financeView ? props.showOverdueInvoices?{financeView:"true", showOverdueInvoices:"true"}:{financeView:"true"}:undefined});

  const [costingDefaultValues, setCostingDefaultValues] = useState({});
  if (!costingDefaultValues || __.isEmpty(costingDefaultValues)) {
    getCostingDefaultValues(apiSitePrefix, SystemData, setCostingDefaultValues);
  }

  // canEdit should be passed from parent in _props
  const [state] = useState({
    canEdit: props.canEdit,
    year: props.year,
    month: props.month
  });


  const [actualCosts, setActualCosts] = useState({
    open: false,
    trip: "",
    tripData: null
  });

  const [tripConfirmation, setTripConfirmation] = useState({
    open: false,
    trip: "",
    action: null,
    requiredConfirmation: null,
    user: ""
  });

  const [selectAirport, setSelectAirport] = useState({
    open: false,
    airport: null,
    mode: "download"
  });

  const [selectCustomer, setSelectCustomer] = useState({
    open: false,
    airline: null,
    mode: "download"
  });

  const defaultReportCriteria = {
    open: false,
    title: null,
    selectionFields: ['dates'],
    fromDate: moment().startOf('month').toDate(),
    toDate: moment().endOf('month').toDate(),
    airline: null,
    airport: null,
    company: null,
    aircraftReg: null,
    includeAllAircraft: false,
    scratchPadUser: user.sub,
    scratchPad: 0,
    fileTransfer: false,
    mode: "download"
  }
  const [reportCriteria, setReportCriteria] = useState({...__.cloneDeep(defaultReportCriteria)});

  const [viewDocuments, setViewDocuments] = useState ({
    open: false,
    documentsToView: null,
  });

  const [defaultValues, setDefaultValues] = useState(null);
  const [scheduleDefaultValues, setScheduleDefaultValues] = useState(null);

  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [defaultValues?.tripOwnerDepartment]});

  const listFinanceActionOptions = [
      {action: "Anticipated Costs Report", itemLabel: "Anticipated Costs Report", itemIcon: <Icon>description_outlined</Icon>, },
      {action: "Finance Budget Report", itemLabel: "Finance Budget Report", itemIcon: <Icon>description_outlined</Icon>, },
    ];

  const listActionOptions = [
    {action: "Download ACMI Operator Flight Schedule", itemLabel: "Download ACMI Operator Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Downloaded"},
    // {action: "Download Air Atlanta Flight Schedule", itemLabel: "Download Air Atlanta Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Downloaded"},
    // {action: "Download Atlas Air Flight Schedule", itemLabel: "Download Atlas Air Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Downloaded"},
    {action: "Download Airport Flight Schedule", itemLabel: "Download Airport Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Downloaded"},
    {action: "Download Customer Flight Schedule", itemLabel: "Download Customer Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Downloaded"},
    {action: "Download Fuel Hours Report", itemLabel: "Download Fuel Hours Report", itemIcon: <LocalGasStation/>, historySuffix: " - Downloaded"},
    {action: "Download Flight Delay Report", itemLabel: "Download Flight Delay Report", itemIcon: <MoreTime/>, historySuffix: " - Downloaded"},
    {action: "Download Block Hours Report", itemLabel: "Download Block Hours Report", itemIcon: <SettingsEthernet/>, historySuffix: " - Downloaded"},
  ];

  const sendActionOptions = [
    {action: "Email ACMI Operator Flight Schedule", itemLabel: "Email ACMI Operator Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Emailed"},
    {action: "Send ACMI Operator SSIM Flight Schedule", itemLabel: "Send ACMI Operator SSIM Flight Schedule", itemIcon: <FlightScheduleIcon style={{width: 24, height: 24 }} color={"primary"}/>, historySuffix: " - Triggered"},
    {action: "Email Air Atlanta Flight Schedule", itemLabel: "Email Air Atlanta Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Emailed"},
    {action: "Email Atlas Air Flight Schedule", itemLabel: "Email Atlas Air Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Emailed"},
    {action: "Email Airport Flight Schedule", itemLabel: "Email Airport Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Emailed"},
    {action: "Email Customer Flight Schedule", itemLabel: "Email Customer Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Emailed"},
    {action: "Send Customer SSIM Flight Schedule", itemLabel: "Send Customer SSIM Flight Schedule", itemIcon: <FlightScheduleIcon/>, historySuffix: " - Emailed"},
    {action: "Send Supplier SSIM Flight Schedule", itemLabel: "Send Supplier SSIM Flight Schedule", itemIcon: <FlightScheduleIcon/>, historySuffix: " - Emailed"},
  ];

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/trip/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Trips Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Trips Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Trip Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Trip Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Trip Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Trip Defaults. Server is not responding`);
    }
  }

  const getScheduleDefaultValues = async (scheduleType) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/flightSchedule/defaults`);
      if (response.data && response.data.success) {
        setScheduleDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Flight Schedule Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Flight Schedule Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Flight Schedule Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Flight Schedule Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Flight Schedule Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Flight Schedule Defaults. Server is not responding`);
    }
  }

  const handleEditTripAction = async (tripUuid, tripNumber) => {
    const url = `${apiSitePrefix}/trip/get/${tripUuid}`;

    if (url) {
      try {
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          eventBus.trigger("EditTripDialog.open", response.data);
        } else {
          SystemData.openAlert(
            "Get Trip Error",
            `Could not get data for Trip ${tripNumber}`
          );
        }
      } catch (err) {
        let errorText = "due to an unknown error";
        if (err.response && err.response.data && err.response.data.error) {
          errorText = `, ${err.response.data.errorMessage}`;
        }
        SystemData.openAlert(
          "Edit Trip Error",
          `Could not edit trip ${tripNumber} ${errorText}`
        );
      }
    }
  }

  const viewContractDocument = (contractDocument, contractName) => {
    const documentsToView = [];
    if (contractDocument && contractDocument !== "") {
      const fileNameParts = contractDocument.split(".");
      const filePathParts = contractDocument.split("/");
      const documentType = fileNameParts && Array.isArray(fileNameParts) && fileNameParts.length > 0 ? fileNameParts[fileNameParts.length - 1] : "";
      const fileName = filePathParts && Array.isArray(filePathParts) && filePathParts.length > 0 ? filePathParts[filePathParts.length - 1] : "";
      const documentDetails = {
        documentDescription: contractName,
        documentType: documentType,
        fileName: fileName,
        fileType: documentType,
        uri: contractDocument
      }
      documentsToView.push(documentDetails);
    }
    if (documentsToView.length > 0) {
      setViewDocuments({...setViewDocuments, open: true, documentsToView: documentsToView});
    }
  }

  const downloadContract = (contractFile) => {
    window.open(contractFile, "_blank");
  }

  const viewAnnex = (fuelAnnexUuid, effectiveDate) => {
    eventBus.trigger("ViewFuelAnnexDialog.open", {fuelAnnexUuid, effectiveDate});
  }

  const editInvoiceDetails = (tripUuid, contractUuid, tripContractInvoiceUuid) => {
    if (!contractUuid) {
      const message = parse(`There is no contract for this trip. Please add contract details in the <strong>'Trip Client Cargo Allotments'</strong> for this trip.`);
      SystemData.openAlert(
        "Edit Invoice Details Error",
        message
      )
      return;
    }
    eventBus.trigger("TripContractInvoiceDialog.open", {tripUuid, contractUuid, tripContractInvoiceUuid});
  }

  async function executeListAction(params) {
    if (params) {
      switch (params.selectedOption) {
        case "Download ACMI Operator Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "ACMI Operator Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'airline', 'scratchPad'],
            onChange: (criteria) => {
              if (criteria) {
                const {airline, fromDate, toDate, applyScratchPad, scratchPadUuid } = criteria;
                downloadFlightSchedule("airline", airline?.data?.airlineCode, true, undefined, undefined, undefined, fromDate, toDate, applyScratchPad, scratchPadUuid);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
//          setSelectCustomer({...selectCustomer, open: true, airline: true, mode: "download"});
          break;
        // case "Download Air Atlanta Flight Schedule":
        //   downloadFlightSchedule("airline", "CC", true);
        //   break;
        // case "Download Atlas Air Flight Schedule":
        //   downloadFlightSchedule("airline", "5Y", true);
        //   break;
        case "Download Airport Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "Airport Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'airport', 'excludeAcmi', 'scratchPad'],
            onChange: (criteria) => {
              if (criteria) {
                const {airport, fromDate, toDate, excludeAcmi, applyScratchPad, scratchPadUuid } = criteria;
                downloadFlightSchedule("airport", airport?.data?.airportCode, true, null, null, excludeAcmi, fromDate, toDate, applyScratchPad, scratchPadUuid);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
          break;
        case "Download Customer Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "Customer Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'customer', 'scratchPad'],
            onChange: (criteria) => {
              if (criteria) {
                const {company, fromDate, toDate, applyScratchPad, scratchPadUuid } = criteria;
                downloadFlightSchedule("customer", company?.data?.companyUuid, true, company?.data?.companyUuid, company?.data?.companyName, undefined, fromDate, toDate, applyScratchPad, scratchPadUuid);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
//          setSelectCustomer({...selectCustomer, open: true, mode: "download"});
          break;
        case "Download Fuel Hours Report":
          downloadFlightSchedule("hoursFuelReport", "", true);
          break;
        case "Download Flight Delay Report":
          downloadFlightSchedule("delayReport", "", true);
          break;
        case "Download Block Hours Report":
          downloadFlightSchedule("hoursBlockReport", "", true);
          break;
        case "Anticipated Costs Report":
          setReportCriteria({
            ...reportCriteria,
            title: "Anticipated Costs Report Options",
            open: true,
            mode: "download",
            selectionFields: ['includeAllAircraft', 'aircraftReg', 'dates'],
            onChange: (criteria) => {
              if (criteria) {
                const {includeAllAircraft, aircraftReg, fromDate, toDate } = criteria;
                // downloadCostReport("costings", aircraftReg, true, fromDate, toDate, "anticipatedCosts");
                requestCostReport(includeAllAircraft, aircraftReg, fromDate, toDate, "anticipatedCosts");
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
          break;
        case "Finance Budget Report":
          setReportCriteria({
            ...reportCriteria,
            title: "Finance Budget Report Options",
            open: true,
            mode: "download",
            selectionFields: ['includeAllAircraft', 'aircraftReg', 'dates'],
            onChange: (criteria) => {
              if (criteria) {
                const {includeAllAircraft, aircraftReg, fromDate, toDate } = criteria;
                // downloadCostReport("costings", aircraftReg, true, fromDate, toDate, "budgetCosts");
                requestCostReport(includeAllAircraft, aircraftReg, fromDate, toDate, "budgetCosts");
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
          break;
        default:
          break;
    }
    }
  }

  async function executeEmailAction(params) {
    if (params) {
      switch (params.selectedOption) {
        case "Email ACMI Operator Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "ACMI Operator Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'airline', 'scratchPad'],
            onChange: (criteria) => {
              if (criteria) {
                const {airline, fromDate, toDate, applyScratchPad, scratchPadUuid } = criteria;
                downloadFlightSchedule("airline", airline?.data?.airlineCode, false, undefined, undefined, undefined, fromDate, toDate, applyScratchPad, scratchPadUuid);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
//          setSelectCustomer({...selectCustomer, open: true, airline: true, mode: "email"});
          break;
        case "Send ACMI Operator SSIM Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "ACMI Operator SSIM Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'airline', 'scratchPad', 'fileTransfer'],
            onChange: (criteria) => {
              if (criteria) {
                const {airline, fromDate, toDate, applyScratchPad, scratchPadUuid, fileTransfer } = criteria;
                ssimFlightSchedule("airline", airline?.data?.airlineCode, false, undefined, undefined, undefined, fromDate, toDate, applyScratchPad, scratchPadUuid, fileTransfer);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
//          setSelectCustomer({...selectCustomer, open: true, airline: true, mode: "email"});
          break;
        // case "Email Air Atlanta Flight Schedule":
        //   downloadFlightSchedule("airline", "CC", false);
        //   break;
        // case "Email Atlas Air Flight Schedule":
        //   downloadFlightSchedule("airline", "5Y", false);
        //   break;
        case "Email Airport Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "Airport Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'airport', 'excludeAcmi', 'scratchPad'],
            onChange: (criteria) => {
              if (criteria) {
                const {airport, fromDate, toDate, excludeAcmi, applyScratchPad, scratchPadUuid } = criteria;
                downloadFlightSchedule("airport", airport?.data?.airportCode, false, null, null, excludeAcmi, fromDate, toDate, applyScratchPad, scratchPadUuid);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
//          setSelectAirport({...selectAirport, open: true, mode: "email"});
          break;
        case "Email Customer Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "Customer Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'customer', 'scratchPad'],
            onChange: (criteria) => {
              if (criteria) {
                const {company, fromDate, toDate, applyScratchPad, scratchPadUuid } = criteria;
                downloadFlightSchedule("customer", company?.data?.companyUuid, false, company?.data?.companyUuid, company?.data?.companyName, undefined, fromDate, toDate, applyScratchPad, scratchPadUuid);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
//          setSelectCustomer({...selectCustomer, open: true, mode: "email"});
          break;
        case "Send Customer SSIM Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "Customer SSIM Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'customer', 'scratchPad', 'fileTransfer'],
            onChange: (criteria) => {
              if (criteria) {
                const {company, fromDate, toDate, applyScratchPad, scratchPadUuid, fileTransfer } = criteria;
                ssimFlightSchedule("customer", company?.data?.companyUuid, false, company?.data?.companyUuid, undefined, undefined, fromDate, toDate, applyScratchPad, scratchPadUuid, fileTransfer);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
          break;
        case "Send Supplier SSIM Flight Schedule":
          setReportCriteria({
            ...reportCriteria,
            title: "Supplier SSIM Flight Schedule Options",
            open: true,
            mode: "download",
            selectionFields: ['dates', 'supplier', 'scratchPad', 'fileTransfer'],
            onChange: (criteria) => {
              if (criteria) {
                const {company, fromDate, toDate, applyScratchPad, scratchPadUuid, fileTransfer } = criteria;
                ssimFlightSchedule("supplier", company?.data?.companyUuid, false, company?.data?.companyUuid, company?.data?.companyName, undefined, fromDate, toDate, applyScratchPad, scratchPadUuid, fileTransfer, true);
              }
              setReportCriteria({...__.cloneDeep(defaultReportCriteria)});
            }
          });
          break;
        default:
          break;
      }
    }
  }

  const convertActionObjectToFlag = (object) => object.toLowerCase().replace(/ /g, "_");

  const setActualCostsCompleted = (rowData) => {
    SystemData.confirm(
      "Indicate Actual Costs Completed",
      `This action will indicate that the actual costs have been captured for this trip and remove the missing cost indicator. Do you wish to continue?`,
      [
        {icon: "check_circle_outline", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      (response) => {
        if (response) {
          SystemData.toggleLoadingProgress(true);
          const confirmations = rowData.confirmations ? JSON.parse(rowData.confirmations) : {};
          confirmations["actualCostsCaptured"] = true;
          const newData = {...rowData, confirmations: confirmations };
          handleUpdateTripCosts.mutate({extraKeys, record: newData, oldRecord: rowData});
          SystemData.toggleLoadingProgress(false);
        }
      }
    );
  }
  const resetActualCostsCompleted = (rowData) => {
    SystemData.toggleLoadingProgress(true);
    const confirmations = rowData.confirmations ? JSON.parse(rowData.confirmations) : {};
    const newData = {...rowData, confirmations: __.omit(confirmations, "actualCostsCaptured") };
    handleUpdateTripCosts.mutate({extraKeys, record: newData, oldRecord: rowData});
    SystemData.toggleLoadingProgress(false);
  }
  const getRowActionOptions = (rowData) => {
    const rowActionOptions = props.financeView ? [
      {action: "Edit Invoice Details", itemLabel: "Edit Invoice Details", itemIcon: <SaveAlt/>},
      {action: "Trip Profitability", itemLabel: "Trip Profitability", itemIcon: <SaveAlt/>, historySuffix: " - Downloaded"},
    ] : [
      // {action: "Trip Sheet", itemLabel: "Generate Trip Sheet", itemIcon: <ListAlt/>, historySuffix: " - Downloaded"},
      {action: "Generate Fuel Analysis", itemLabel: "Generate Fuel Analysis", itemIcon: <Icons>local_gas_station</Icons>},
      {action: "Trip Profitability", itemLabel: "Trip Profitability", itemIcon: <SaveAlt/>, historySuffix: " - Downloaded"},
      // {action: "EuroCargo Reconciliation", itemLabel: "EuroCargo Reconciliation", itemIcon: <SaveAlt/>, historySuffix: " - Downloaded"},
      // {action: "EuroCargo Draft Reconciliation", itemLabel: "EuroCargo Draft Reconciliation", itemIcon: <SaveAlt/>, historySuffix: " - Downloaded"},
      // {action: "Confirm EuroCargo Draft Reconciliation", itemLabel: "Confirm EuroCargo Draft Reconciliation", itemIcon: <DoneAll/>, historySuffix: " - Done"},
      // {action: "View Audit Trail", itemLabel: "View Audit Trail", itemIcon: <History/>, historySuffix: ""},
    ];
    if (rowData.contractDocument) {
      rowActionOptions.push({action: "View Contract", itemLabel: "View Contract", itemIcon: <Icon>description_outlined</Icon>, historySuffix: ""});
      rowActionOptions.push({action: "Download Contract", itemLabel: "Download Contract", itemIcon: <Icon>get_app</Icon>, historySuffix: ""});
    }
    if (rowData.uniqueCostingNo) {
      rowActionOptions.push({action: "View Costing", itemLabel: "View Costing", itemIcon: <Preview/>, historySuffix: ""});
      rowActionOptions.push({action: "Edit Costing", itemLabel: "Edit Costing", itemIcon: <MonetizationOn/>, historySuffix: ""});
      const confirmations = rowData.confirmations ? typeof rowData.confirmations === "string" ? JSON.parse(rowData.confirmations) : rowData.confirmations : null;
      if (!rowData.confirmations || !confirmations?.actualCostsCaptured) {
        rowActionOptions.push({action: "Set Actual Costs Completed", itemLabel: "Set Actual Costs Completed", itemIcon: <GridOn/>, historySuffix: ""});
      } else {
        rowActionOptions.push({action: "Reset Actual Costs Completed", itemLabel: "Reset Actual Costs Completed", itemIcon: <RestartAlt/>, historySuffix: ""});
      }
    }
    if (rowData.fuelAnnexUuid) {
      rowActionOptions.push({action: "View Fuel Annex", itemLabel: "View Fuel Annex", itemIcon: <Preview/>, historySuffix: ""});
    }
    return rowActionOptions;
  }
  // if (props.canEdit) {
  //   rowActionOptions.unshift(
  //     {action: "Create Trip Folders", itemLabel: "Create Trip Folders", itemIcon: <FolderOpen/>, historySuffix: " - Done"}
  //   );
  // }

  async function executeRowAction(params) {
    if (params) {
      const rowData = params.rowData;
      let url = null;

      switch (params.selectedAction) {
        case "Generate Trip Sheet":
          url = `${apiSitePrefix}/trip/tripsheet/${rowData.tripUuid}`;
          break;
        case "Create Trip Folders":
          url = `${apiSitePrefix}/trip/createfolders/${rowData.tripUuid}`;
          break;
        case "Trip Profitability":
          url = `${apiSitePrefix}/trip/summaryexcel/${rowData.tripPrefix}/${rowData.tripNo}`;
          break;
        case "EuroCargo Reconciliation":
        case "EuroCargo Draft Reconciliation":
          url = `${apiSitePrefix}/trip/eurocargo/${rowData.tripPrefix}/${rowData.tripNo}`;
          break;
        case "Confirm EuroCargo Draft Reconciliation":
          const userName = renderUserName(user);
          setTripConfirmation({...tripConfirmation,
                                     open: true,
                                     trip:rowData.tripPrefix + rowData.tripNo,
                                     action: params.selectedOption,
                                     requiredConfirmation: "Review and Correct",
                                     user: userName,
                                     rowData: rowData,
                                    });
          break;
        case "View Audit Trail":
          SystemData.openAuditTrail(rowData.tripUuid, `Audit Trail for ${rowData.tripPrefix}${rowData.tripNo}`);
          break;
        case "Generate Fuel Analysis":
          url = `${apiSitePrefix}/trip/fuelanalysis/${rowData.tripPrefix}/${rowData.tripNo}`;
          break;
        case "View Costing":
          viewCosting(rowData);
          break;
        case "Edit Costing":
          editCosting(rowData);
          break;
        case "Edit Invoice Details":
          editInvoiceDetails(rowData.tripUuid, rowData.contractUuid, rowData.tripContractInvoiceUuid);
          break;
        case "View Contract":
          viewContractDocument(rowData.contractDocument, rowData.contractName);
          break;
        case "View Fuel Annex":
          viewAnnex(rowData.fuelAnnexUuid, rowData.tripDate);
          break;
        case "Download Contract":
          downloadContract(rowData.contractDocument);
          break;
        case "Anticipated Costs Report":
          break;
        case "Finance Budget Report":
          break;
        case "Set Actual Costs Completed":
          setActualCostsCompleted(rowData);
          break;
        case "Reset Actual Costs Completed":
          resetActualCostsCompleted(rowData);
          break;
        default:
          break;
      }

      if (url) {
        SystemData.toggleLoadingProgress(true, `Generating Report...`);

        try {
          const response = await axios.get(url, {
            headers: {
              //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
              'Access-Control-Allow-Origin': '*',
            }
          });

          if (response && response.data) {
            SystemData.toggleLoadingProgress(false);
            if (response.data.status) {
              const fileName = response.data.downloadFileName;
              let a = document.createElement('a');
              a.href = response.data.excelSignedUrl;
              a.download = fileName;
              a.style.display = "none";
              document.body.appendChild(a);
              a.click();
              setTimeout(() => { document.body.removeChild(a); }, 0);
            } else {
              if (response.data.error) {
                switch (params.selectedAction) {
                  case "Generate Trip Sheet":
                  case "Create Trip Folders":
                  case "Confirm EuroCargo Draft Reconciliation":
                    SystemData.openAlert(
                      "Error",
                      `${params.selectedAction} failed to action due to the following error: ${response.data.error}`
                    );
                    break;
                  default:
                    SystemData.openAlert(
                      "Download Error",
                      `The ${params.selectedAction} report could not be downloaded due to the following error: ${response.data.error}`
                    );
                }
              } else {
                SystemData.openAlert(
                  "Info",
                  `${params.selectedAction} Completed`
                )
              }
            }
          }

        } catch (err) {
          SystemData.toggleLoadingProgress(false);
          let errorText = "due to an unknown error";
          if (err.response && err.response.data && err.response.data.error) {
            errorText = `, ${err.response.data.errorMessage}`;
          }
          switch (params.selectedAction) {
            case "Create Trip Folders":
            case "Confirm EuroCargo Draft Reconciliation":
              SystemData.openAlert(
                "Error",
                `${params.selectedAction} failed to action ${errorText}`
              );
              break;
            default:
              SystemData.openAlert(
                "Download Error",
                `The ${params.selectedAction} report could not be downloaded ${errorText}`
              );
          }
        }
      }
      queryClient.invalidateQueries(['Trips', ...extraKeys]);
      queryClient.invalidateQueries(["Trips", "Count", "REVIEW"]);
    }
  }

  const refreshList = () => {
    queryClient.invalidateQueries(['Trips', ...extraKeys]);
    queryClient.invalidateQueries(["Trips", "Count", "REVIEW"]);
  } 
  
  async function requestCostReport(includeAllAircraft, aircraftTailNumber, fromDate, toDate, reportType = "anticipatedCosts") {

    const startDateMoment = fromDate ? moment(fromDate) : moment().startOf("month");
    const startDate = startDateMoment.isValid() ? startDateMoment.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
    const endDateMoment = toDate? moment(toDate) : moment().endOf("month");
    const endDate = endDateMoment.isValid() ? endDateMoment.format("YYYY-MM-DD") : moment().endOf("month").format("YYYY-MM-DD");

    SystemData.requestBackgroundReport(reportType, {includeAllAircraft, aircraftTailNumber, startDate, endDate});
  }

  async function downloadCostReport(target, aircraftReg, download = true, fromDate, toDate, reportType = "anticipatedCosts") {

    if (!target)
      return;

    const startDateMoment = fromDate ? moment(fromDate) : moment().startOf("month");
    const startDate = startDateMoment.isValid() ? startDateMoment.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
    const endDateMoment = toDate? moment(toDate) : moment().endOf("month");
    const endDate = endDateMoment.isValid() ? endDateMoment.format("YYYY-MM-DD") : moment().endOf("month").format("YYYY-MM-DD");

    let url = `${apiSitePrefix}/${target}/${reportType}/${startDate}/${endDate}/${aircraftReg}`;

    if (url) {
      try {
        SystemData.toggleLoadingProgress(true, `Preparing Report ...`);
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          if (response.data.status) {
            SystemData.toggleLoadingProgress(false);
            if (!download) {
              let templateType;
              let subject;
              let attachmentDescription;
              const displayDateFormat = "DD MMM YYYY";
              const scheduleFrom = startDateMoment.isValid() ? startDateMoment.format(displayDateFormat) : moment().format(displayDateFormat);
              const scheduleTo = moment(scheduleFrom, displayDateFormat).endOf("month").format(displayDateFormat);
              const fileName = response.data.downloadFileName;
              switch (target) {
                default:
                  subject = `Finance Budget Report // ${scheduleFrom} to ${scheduleTo} ${aircraftReg ? `// ${aircraftReg}` : ""}`;
                  templateType = "";
                  attachmentDescription = `${subject}`;
                  break;
              }

              const emailRecord = {
                target: target,
                targetFilter: aircraftReg,
                recipients: "",
                carbonCopy: "",
                subject: subject,
                templateType: templateType,
                templateUuid: "dummyTemplateUuid",
                attachmentDescription: attachmentDescription,
                attachmentUrl: response.data.excelSignedUrl,
                fileName: fileName.replaceAll("/",""),
                repeatData: [],
                contactRole: ""
              }
              SystemData.sendEmail(emailRecord, `Send Report`, true);
              return;
            }

            const fileName = response.data.downloadFileName;
            let a = document.createElement('a');
            a.href = response.data.excelSignedUrl;
            a.download = fileName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); }, 0);
          } else {
            SystemData.toggleLoadingProgress(false);
            SystemData.openAlert(
              "Download Error",
              `The report could not be downloaded due to the following error: ${response.data.error}`
            );
          }
        }
      } catch (err) {
        SystemData.toggleLoadingProgress(false);
        SystemData.openAlert(
          "Download Error",
          `The report could not be downloaded due to the following unexpected error: ${err}`
        );
      }
    }
  }

  async function downloadFlightSchedule(target, filter, download = true, companyUuid = null, companyName = "", excludeAcmi = false, startDate = null, endDate = null, applyScratchPad = false, scratchPadUuid = null) {

    if (!target)
      return;

    const reportStartDate = startDate || moment(`${props.year}-${props.month}-01`).format("YYYY-MM-DD");
    const reportEndDate = endDate || moment(reportStartDate).endOf("month").format("YYYY-MM-DD");
    const route = target === "delayReport" ? "delayReport" : target === "hoursFuelReport" ? "hoursFuelReport" : target === "hoursBlockReport" ? "hoursBlockReport" : target === "airline" ? "lessorschedule" : target==="customer"?"customerschedule":"airportschedule";

    let url = `${apiSitePrefix}/trip/${route}/${reportStartDate}/${reportEndDate}/${filter}${excludeAcmi ? "/excludeAcmi" : ""}${applyScratchPad && scratchPadUuid ? "?scratchPadUuid=" + scratchPadUuid : ""}`;

    if (url) {
      try {
        SystemData.toggleLoadingProgress(true, `Preparing Flight Schedule Report ...`);
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          SystemData.toggleLoadingProgress(false);
          if (response.data.status) {
            if (!download) {
              let templateType;
              let subject;
              let attachmentDescription;
              const displayDateFormat = "DD MMM YYYY";
              const startDateMoment = moment(`${props.year}-${props.month}-01`);
              const scheduleFrom = startDateMoment.isValid() ? startDateMoment.format(displayDateFormat) : moment().format(displayDateFormat);
              const scheduleTo = moment(scheduleFrom, displayDateFormat).endOf("month").format(displayDateFormat);
              const fileName = response.data.downloadFileName;
              switch (target) {
                case "airline":
                  subject = `${operatorName} Flight Schedule // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "OPERATOR_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                case "customer":
                  subject = `${operatorName} Flight Schedule // ${companyName} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "CUSTOMER_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule for ${companyName} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                default:
                  subject = `${operatorName} Flight Schedule // ${filter} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "AIRPORT_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule via ${filter} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
              }

              const emailRecord = {
                target: target,
                targetFilter: target === "airport" && companyUuid ? companyUuid : filter,
                recipients: "",
                carbonCopy: scheduleDefaultValues && __.has(scheduleDefaultValues, "ccList") ? scheduleDefaultValues.ccList : "",
                subject: subject,
                templateType: templateType,
                templateUuid: "dummyTemplateUuid",
                attachmentDescription: attachmentDescription,
                attachmentUrl: response.data.excelSignedUrl,
                fileName: fileName.replaceAll("/",""),
                repeatData: [],
                contactRole: "FLIGHTS",
                airportCode: target === "airport"?filter:undefined,
                from: scheduleDefaultValues && __.has(scheduleDefaultValues, "from") ? scheduleDefaultValues.from : "",
              }
              SystemData.sendEmail(emailRecord, `Send Flight Schedule`, true);

              return;
            }

            const fileName = response.data.downloadFileName;
            let a = document.createElement('a');
            a.href = response.data.excelSignedUrl;
            a.download = fileName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); }, 0);
          } else {
            SystemData.openAlert(
              "Download Error",
              `The Schedule report could not be downloaded due to the following error: ${response.data.error}`
            );
          }
        }
      } catch (err) {
        SystemData.toggleLoadingProgress(false);
        SystemData.openAlert(
          "Download Error",
          `The Schedule report could not be downloaded due to the following unexpected error: ${err}`
        );
      }
    }
  }

  async function ssimFlightSchedule(target, filter, download = true, companyUuid = null, companyName = "", excludeAcmi = false, startDate = null, endDate = null, applyScratchPad = false, scratchPadUuid = null, fileTransfer = false, supplier = false) {

    if (!target)
      return;

    const reportStartDate = startDate || moment(`${props.year}-${props.month}-01`).format("YYYY-MM-DD");
    const reportEndDate = endDate || moment(reportStartDate).endOf("month").format("YYYY-MM-DD");
    const route = "SSIM";

    const urlParams = [];
    if (applyScratchPad && scratchPadUuid)
      urlParams.push("scratchPadUuid=" + scratchPadUuid);
    if (companyUuid)
      urlParams.push("customerUuid=" + companyUuid);
    if (fileTransfer)
      urlParams.push("fileTransfer=" + fileTransfer);
    if (supplier)
      urlParams.push("allFlights=" + true);

    let url = `${apiSitePrefix}/trip/${route}/${reportStartDate}/${reportEndDate}/${filter}${excludeAcmi ? "/excludeAcmi" : ""}${urlParams.length > 0 ? "?" + urlParams.join("&") : ""}`;

    if (url) {
      try {
        SystemData.toggleLoadingProgress(true, `Preparing SSIM Flight Schedule File ...`);
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          SystemData.toggleLoadingProgress(false);
          if (response.data.status) {
            if (!download) {
              let templateType;
              let subject;
              let attachmentDescription;
              const displayDateFormat = "DD MMM YYYY";
              const startDateMoment = moment(`${props.year}-${props.month}-01`);
              const scheduleFrom = startDateMoment.isValid() ? startDateMoment.format(displayDateFormat) : moment().format(displayDateFormat);
              const scheduleTo = moment(scheduleFrom, displayDateFormat).endOf("month").format(displayDateFormat);
              const fileName = response.data.downloadFileName;
              switch (target) {
                case "airline":
                  subject = `${operatorName} SSIM Flight Schedule // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "OPERATOR_FLIGHTS";
                  attachmentDescription = `${operatorName} SSIM flight schedule effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                case "customer":
                  subject = `${operatorName} SSIM Flight Schedule // ${companyName} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "CUSTOMER_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                case "supplier":
                  subject = `${operatorName} SSIM Flight Schedule // ${companyName} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "CUSTOMER_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                default:
                  subject = `${operatorName} SSIM Flight Schedule // ${filter} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "AIRPORT_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule via ${filter} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
              }

              if (fileTransfer) {
                SystemData.openAlert(
                  "Upload Success",
                  `${attachmentDescription} ${response.data.message}`
                );

                return;
              }

              const emailRecord = {
                target: target,
                targetFilter: target === "airport" && companyUuid ? companyUuid : filter,
                recipients: "",
                carbonCopy: scheduleDefaultValues && __.has(scheduleDefaultValues, "ccList") ? scheduleDefaultValues.ccList : "",
                subject: subject,
                templateType: templateType,
                templateUuid: "dummyTemplateUuid",
                attachmentDescription: attachmentDescription,
                attachmentUrl: response.data.signedUrl,
                fileName: fileName.replaceAll("/",""),
                repeatData: [],
                contactRole: "FLIGHTS",
                airportCode: target === "airport"?filter:undefined,
                from: scheduleDefaultValues && __.has(scheduleDefaultValues, "from") ? scheduleDefaultValues.from : "",
              }
              SystemData.sendEmail(emailRecord, `Send SSIM Flight Schedule`, true);

              return;
            }
            const fileName = response.data.downloadFileName;
            let a = document.createElement('a');
            a.href = response.data.excelSignedUrl;
            a.download = fileName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); }, 0);
          } else {
            SystemData.openAlert(
              "Download Error",
              `The SSIM Schedule could not be downloaded due to the following error: ${response.data.error}`
            );
          }
        }
      } catch (err) {
        SystemData.toggleLoadingProgress(false);
        SystemData.openAlert(
          "Download Error",
          `The SSIM Schedule could not be downloaded due to the following unexpected error: ${err}`
        );
      }
    }
  }


  async function downloadTripProfitabilityData(rowData) {

    const url = `${apiSitePrefix}/trip/profitability/${rowData.tripPrefix}/${rowData.tripNo}`;
    SystemData.toggleLoadingProgress(true, `Loading trip profitability data ...`);
    await axios.get(url, {
        headers: {
          //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
          'Access-Control-Allow-Origin': '*',
        }
      })
      .then(response => {
        SystemData.toggleLoadingProgress(false);
        if (response && response.data && !response.data.error) {
          setActualCosts({...actualCosts, open: true, trip: rowData.tripPrefix+rowData.tripNo, tripData: response.data, rowData: rowData})
        }
        else {
          const error = response.data.error ? response.data.errorMessage : "";
          SystemData.openAlert("Data not Available", `Could not retrieve MCS data for trip ${rowData.tripPrefix+rowData.tripNo} - ${error}`);
        }
      })
      .catch(error => {
        SystemData.toggleLoadingProgress(false);
        if (error.response && error.response.data && error.response.data.error) {
          SystemData.openAlert("Data not Available", `Could not retrieve data for trip ${rowData.tripPrefix+rowData.tripNo} - ${error.response.data.errorMessage}`);
        } else {
          SystemData.openAlert("Data not Available", `An unexpected error occurred retrieving data for trip ${rowData.tripPrefix+rowData.tripNo} - ${error}`);
        }
      });
  }

  async function updateActualCosts(tripActualCosts) {
    if (tripActualCosts) {
      const newData = {...actualCosts.rowData, actualCostData: tripActualCosts};
      handleUpdateTripCosts.mutate({extraKeys, record: newData, oldRecord: actualCosts.rowData});
    }

    setActualCosts({ ...actualCosts, open: false, rowData: null });
  }

  async function saveConfirmation(rowData, flagName) {
    const flags = rowData.flags || {};
    if (flagName) {
      flags[flagName] = true;
      const newData = {...rowData, flags};
      handleUpdateTrip.mutate({extraKeys, record: newData, oldRecord: rowData});
    }
  }

  async function updateConfirmation(confirmation) {
    if (confirmation && confirmation.object){
      const flagName = convertActionObjectToFlag(confirmation.object);
      await saveConfirmation(tripConfirmation.rowData, flagName);
    }
    setTripConfirmation({ ...tripConfirmation, open: false, trip: null, action: null, requiredConfirmation: null, user: null });
  }

  const formatChars={
    "U": "[A-Za-z]",
    "A": "[A-Z0-9]",
    "a": "[A-Za-z0-9]",
    "9": "[0-9]",
    "D": "[0-9.]",
    "n": "-",
    "x": "."
  }

  const viewCosting = async (record) => {
    if (record.uniqueCostingNo) {
      const costingRecord = await getCostingByUcn(record.uniqueCostingNo, apiSitePrefix, SystemData);
      if (costingRecord) {
        eventBus.trigger("ViewCostingDialog.open", costingRecord);
      }
    }
  }

  const editCosting = async (record) => {
    if (record.uniqueCostingNo) {
      const costingRecord = await getCostingByUcn(record.uniqueCostingNo, apiSitePrefix, SystemData);
      if (costingRecord) {
        eventBus.trigger("EditCostingDialog.open", costingRecord);
      }
    }
  }

  const handleRouteClick = (rowData) => {
    if (rowData) {
      const {tripUuid, tripPrefix, tripNo} = rowData;
      eventBus.trigger("FinanceTripLegsDialog.open", {tripUuid, tripPrefix, tripNo});
    }
  }

  const handleIgnoreChangesClick = async (e, selectedRecord) => {
    SystemData.confirm(
      `Ignore Changes`,
      `Are you sure you wish to ignore the reqired changes?'`,
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      async (response) => {
        if (response) {
          try {
            SystemData.toggleLoadingProgress(true, "Ignoring change");
            const response = await axios.put(`${apiSitePrefix}/trip/ignoreChange`, [selectedRecord]);
            SystemData.toggleLoadingProgress(false);
            if (!response.data) {
              SystemData.alert("Error", `Could not ignore change. Server is not responding`);
            } else if (response.data.success) {
              queryClient.invalidateQueries(['Trips', ...extraKeys]);
              queryClient.invalidateQueries(["Trips", "Count", "REVIEW"]);
            } else {
              SystemData.alert("Error", `Could not ignore change. Server returned ${response.data.error}`);
            }
          } catch (err) {
            SystemData.toggleLoadingProgress(false);
            SystemData.alert("Error", `Could not ignore change. Server is not responding`);
          }
        }
      }
    );
  }

  const getFinanceTripColumns = (columnProps) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: columnProps.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const validatorColumn = ValidatorColumn(TripsValidator, false);

    const financeColumns = [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Trip Number",
        field: "tripNo",
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.padStart(3,'0').toUpperCase();
          return `${rowData.tripNo}`.endsWith(termUpper);
        },
        render: (rowData) => (rowData.rowNo && rowData.rowNo > 0 ? "" : `${rowData.tripPrefix}${rowData.tripNo}`)
      },
      {
        ...baseColumn,
        ...ColumnWidth(50),
        title: "Trip Date",
        field: "tripDate",
        type: "date",
        format: "dd MMM",
        customFilterAndSearch: dateSearch('tripDate'),
        render: (rowData) => (rowData.rowNo && rowData.rowNo > 0 ? "" : moment.utc(rowData.tripDate).format("DD MMM"))
      },
      {
        ...baseColumn,
        ...ColumnWidth(180),
        title: "Route",
        field: "tripRoute",
        editable: "never",
        render: (rowData) => (rowData.rowNo && rowData.rowNo > 0 ? "" :
          <Link onClick={() => handleRouteClick(rowData)}
            component="button"
            variant="body2">
            <div style={{maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
              {rowData.tripRoute}
            </div>
          </Link>
          )
      },
      {
        ...baseColumn,
        ...ColumnWidth(140),
        title: "Account Manager",
        field: "commercialOwnerId",
        editable: "never",
        render: (rowData) => {
          const userInfo = __.find(departmentUsers?.data, {user_id: rowData.commercialOwnerId});
          return (userInfo ? <div style={{maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{`${userInfo.firstName} ${userInfo.lastName}`}</div> : <></>)
        }
      },
      {
        ...baseColumn,
        // ...ColumnWidth(250),
        title: "Client",
        field: "companyName",
        editable: "never",
        render: (rowData) => (<div style={{overflow: "hidden", textOverflow: "ellipsis"}}>{rowData.companyName}</div>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Costing Number",
        field: "uniqueCostingNo",
        render: (rowData) => {
          return rowData.uniqueCostingNo?(
            <ContextMenu
              menuItems={[
                {code: "EDIT", label: "Edit Costing", icon: <MonetizationOn/>, action: ()=>{editCosting(rowData)}},
                {code: "VIEW", label: "View Costing", icon: <Preview/>, action: ()=>{viewCosting(rowData)}},
              ]}
            >
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  viewCosting(rowData);
                }}
              >
                {rowData.uniqueCostingNo}
              </Link>
            </ContextMenu>
          ) : '';
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Contract",
        field: "contractName",
        editable: "never",
        render: (rowData) => (
          <Tooltip classes={{tooltip: rowData.fuelAnnexUuid?classes.contractTooltip:classes.contractTooltipSmall}} interactive={true} arrow={true} placement="top" title={
            <div style={{width: rowData.fuelAnnexUuid?350:250, fontSize: 12}}>
              <Grid container justifyContent="center" spacing={2}>
                <Grid item xs={12}><div style={{fontWeight: "bold", textAlign: "center"}}>{rowData.contractName}</div></Grid>
                <Grid item xs={4}><div style={{fontWeight: "500"}}>Reference:</div></Grid>
                <Grid item xs={8}><div>{rowData.contractReference}</div></Grid>
                <Grid item xs={4}><div style={{fontWeight: "500"}}>Revision:</div></Grid>
                <Grid item xs={8}><div>{rowData.revision}</div></Grid>
                <Grid item xs={4}><div style={{fontWeight: "500"}}>Period:</div></Grid>
                <Grid item xs={8}><div>{moment.utc(rowData.contractStartDate).format("DD MMM YYYY")} - {moment.utc(rowData.contractEndDate).format("DD MMM YYYY")}</div></Grid>
              </Grid>
              {rowData.fuelAnnexUuid ? (
                <Grid container justifyContent="center" spacing={2}>
                  <Grid item xs={4}>
                      <Button size="small" variant="contained" startIcon={(<Icon>description_outlined</Icon>)} onClick={() => viewContractDocument(rowData.contractDocument, rowData.contractName)} color="primary" fullWidth>
                      View
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button size="small" variant="contained" startIcon={(<Icon>get_app</Icon>)} onClick={() => downloadContract(rowData.contractDocument)} color="primary" fullWidth>
                      Download
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button size="small" variant="contained" startIcon={(<Icon>description_outlined</Icon>)} onClick={() => viewAnnex(rowData.fuelAnnexUuid, rowData.tripDate)} color="primary" fullWidth>
                      Annex
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid container justifyContent="center" spacing={2}>
                  <Grid item xs={6}>
                      <Button size="small" variant="contained" startIcon={(<Icon>description_outlined</Icon>)} onClick={() => viewContractDocument(rowData.contractDocument, rowData.contractName)} color="primary" fullWidth>
                      View
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button size="small" variant="contained" startIcon={(<Icon>get_app</Icon>)} onClick={() => downloadContract(rowData.contractDocument)} color="primary" fullWidth>
                      Download
                    </Button>
                  </Grid>
                </Grid>
              )
            }
            </div>
          }>
            <div style={{maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{rowData.contractReference||rowData.contractName}</div>
          </Tooltip>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Contracted Amount",
        field: "contractedAmount",
        editable: "always",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={2}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.contractedAmount}
              prefix="$ "
              allowNegative={false}
            />
          </div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Invoice Number",
        field: "invoiceNo",
        editable: "always",
        render: (rowData) => (<div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>{rowData.invoiceNo}</div>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Invoice Amount",
        field: "invoiceAmount",
        editable: "always",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={2}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.invoiceAmount}
              prefix="$ "
              allowNegative={false}
            />
          </div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Due Date",
        field: "invoiceDueDate",
        editable: "always",
        render: (rowData) => (<div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>{moment(rowData.invoiceDueDate).format("DD MMM YYYY")}</div>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Status",
        field: "invoiceStatus",
        emptyValue: "OUTSTANDING",
        defaultValue: "OUTSTANDING",
        initialEditValue: "OUTSTANDING",
        editComponent: (props) => (
          <ComboSelect
            id="invoiceStatus"
            group="INVOICE TRACKING"
            subGroup="STATUS"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            id="invoiceStatus"
            group="INVOICE TRACKING"
            subGroup="STATUS"
            fullWidth
            value={rowData.invoiceStatus || "OUTSTANDING"}
            displayOnly={true}
          />
        ),
      },
    ];
    return financeColumns;
  };


  const getTripColumns = (columnProps) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: columnProps.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const validatorColumn = ValidatorColumn(TripsValidator, false);

    const tripColumns = [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(50),
        cellStyle: { padding: "8px 2px 8px 12px" },
        headerStyle: { padding: "8px 2px 8px 12px" },
        title: "Trip Prefix",
        field: "tripPrefix",
        initialEditValue: process.env.REACT_APP_TRIP_PREFIX || "MAG",
        emptyValue: process.env.REACT_APP_TRIP_PREFIX || "MAG",
        editable: "never",
        render: (rowData) => (rowData.rowNo && rowData.rowNo > 0 ? "" : rowData.tripPrefix)
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Trip Number",
        field: "tripNo",
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.padStart(3,'0').toUpperCase();
          return `${rowData.tripNo}`.endsWith(termUpper);
        },
        render: (rowData) => (rowData.rowNo && rowData.rowNo > 0 ? "" : rowData.tripNo)
      },
      {
        ...baseColumn,
        ...ColumnWidth(90),
        title: "Trip Date",
        field: "tripDate",
        type: "date",
        format: "dd MMM",
        customFilterAndSearch: dateSearch('tripDate'),
        render: (rowData) => (rowData.rowNo && rowData.rowNo > 0 ? "" : moment.utc(rowData.tripDate).format("DD MMM"))
      },
      {
        ...baseColumn,
        ...ColumnWidth(250),
        title: "Route",
        field: "legs",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const legs = rowData.legs;
          const termUpper = `${term}`.toUpperCase();
          for (let i = 0; i < legs.length; i++) {
            const leg = legs[i];
            const {airlineCode, flightNo, originAirportCode, destinationAirportCode} = {...emptyLeg, ...leg};
            if (originAirportCode === termUpper) return true;
            if (destinationAirportCode === termUpper) return true;
            if (`${flightNo}` === termUpper) return true;
            if (`${airlineCode}${flightNo}` === termUpper) return true;
          }
          return false;
        },
        render: (rowData) => (<BuildPrimaryRouteFromLegs legs={rowData.legs}/>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(70),
        title: "Onward",
        field: "legs",
        editable: "never",
        render: (rowData) => (
          <div>
            <BuildOnwardRouteIconFromLegs legs={rowData.legs} style={{paddingRight: 10}}/>
            <BuildTruckRouteIconFromLegs legs={rowData.legs} style={{paddingRight: 10}}/>
          </div>
        )
      },
      {
        ...baseColumn,
        title: "Flights",
        field: "legs",
        editable: "never",
        render: (rowData) => (<BuildFlightListFromLegs legs={rowData.legs}/>)
      },
    ];
    if (props.showReview) {
      tripColumns.push({
        ...baseColumn,
        title: "Reason",
        field: "reason",
        editable: "never",
      });
    } else {
      tripColumns.push({
        ...baseColumn,
        ...ColumnWidth(140),
        title: "GSA Trip",
        field: "gsaTrip",
        editable: "never",
        render: rowData => <Checked style={{padding: "0px 8px"}} checked={rowData.gsaTrip === true || rowData.gsaTrip === 1} value="true" fontSize="small"/>,
      });
      tripColumns.push({
        ...baseColumn,
        ...ColumnWidth(180),
        title: "Account Manager",
        field: "commercialOwnerId",
        editable: "never",
        render: (rowData) => {
          const userInfo = __.find(departmentUsers?.data, {user_id: rowData.commercialOwnerId});
          return (userInfo ? <span>{`${userInfo.firstName} ${userInfo.lastName}`}</span> : <></>)
        }
      });
      tripColumns.push({
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Costing Number",
        field: "uniqueCostingNo",
        render: (rowData) => {
          return rowData.uniqueCostingNo?(
            <ContextMenu
              menuItems={[
                {code: "EDIT", label: "Edit Costing", icon: <MonetizationOn/>, action: ()=>{editCosting(rowData)}},
                {code: "VIEW", label: "View Costing", icon: <Preview/>, action: ()=>{viewCosting(rowData)}},
              ]}
            >
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  viewCosting(rowData);
                }}
              >
                {rowData.uniqueCostingNo}
              </Link>
            </ContextMenu>
          ) : '';
        }
      });
    }

    return tripColumns;
  };

//  const {addTrip, editTrip, toggleCancelled, dialogBoxes} = editDialogs(SystemData, apiSitePrefix, year, month, extraKeys, handleAddTrip, handleUpdateTrip, formatChars);
  const {
    toggleCancelled,
    AddTripDialog, openAddTrip,
    //EditTripDialog, openEditTrip,
  } = dialogs(SystemData, apiSitePrefix, extraKeys, handleAddTrip, handleUpdateTrip, null, formatChars, classes, defaultValues?.tripOwnerDepartment);

  if (!defaultValues || __.isEmpty(defaultValues))
    getDefaultValues();
    if (!scheduleDefaultValues || __.isEmpty(scheduleDefaultValues))
    getScheduleDefaultValues();

  const rowActions = props.financeView ? [
    // (rowData) => (
    //   {
    //     hidden: false,
    //     disabled: (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === ""),
    //     icon: 'grid_on',
    //     iconProps: { style: (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === "") ? {color: '#b3b0b0'}: { color: theme.palette.primary.main } },
    //     tooltip: (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === "") ? 'Missing Costing' : 'Update Actual Trip Costs',
    //     isFreeAction: false,
    //     onClick: (e, rowData) => {
    //       downloadTripProfitabilityData(rowData);
    //     }
    //   }
    // ),
    (rowData) => (
      {
        hidden: false,
        disabled: (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === ""),
        icon: () => {
          const confirmations = rowData.confirmations ? typeof rowData.confirmations === "string" ? JSON.parse(rowData.confirmations) : rowData.confirmations : null;
          const override = confirmations && __.has(confirmations, "actualCostsCaptured");
          const summary = missingActualCosting(rowData?.tripRoute, rowData?.actualCostData);
          const tooltip = (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === "") ? 'Missing Costing' : `Update Actual Trip Costs (${override ? 'Missing actual cost indicator was overridden' : summary[1]})`;
          console.log('summary', summary);
          return(
            <Tooltip title={tooltip}>
              <Badge
                badgeContent={summary[0]}
                color="error"
                variant="string"
                invisible={rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === "" || override}
                // sx={{ "& .MuiBadge-badge": { fontSize: 5, height: 15, minWidth: 15 } }}
                // style={{padding: '0.1rem'}}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                  <Icon style={{ color: (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === "") ? '#b3b0b0' : theme.palette.primary.main }}>
                    <GridOn/>
                  </Icon>
              </Badge>
            </Tooltip>
          )
        },
        // tooltip: (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === "") ? 'Missing Costing' : 'Update Actual Trip Costs',
        isFreeAction: false,
        onClick: (e, rowData) => {
          downloadTripProfitabilityData(rowData);
        }
      }
    ),
    (rowData) => (
      {
        hidden: false,
        disabled: false,
        icon: () => {
          const  flags = rowData.flags ? rowData.flags : {};
          const rowActions = [...getRowActionOptions(rowData)];
          if ((flags['trip_folders_created'])) {
            __.remove(rowActions, function(item) {
              return (item.action === "Create Trip Folders");
            });
          }
          if ( !(flags['eurocargo_draft_reconciliation_downloaded'])) {
            __.remove(rowActions, function(item) {
              return (item.action === "Confirm EuroCargo Draft Reconciliation");
            });
          }
          if ((flags['confirm_eurocargo_draft_reconciliation'])) {
            __.remove(rowActions, function(item) {
              return (item.action === "Confirm EuroCargo Draft Reconciliation" || item.action === "EuroCargo Draft Reconciliation");
            });
          } else {
            __.remove(rowActions, function(item) {
              return (item.action === "EuroCargo Reconciliation");
            });
          }
          return(
          <IconButtonMenu
            icon={<PlaylistAddCheck/>}
            size={"small"}
            offset={-1}
            style={{ color: theme.palette.primary.main }}
            tooltip={"Trip Actions and Reports"}
            menuItems={rowActions}
            callback={executeRowAction}
            callbackParams={{rowData: rowData}}
          />)
        },
        isFreeAction: false,
      }),
      {
        hidden: false,
        disabled: false,
        icon: () => {
          return(
            <IconButtonMenu
              icon={<SaveAlt/>}
              size={"small"}
              offset={-1}
              style={{ color: theme.palette.primary.main }}
              tooltip={"Download Anticipated Costs Report"}
              menuItems={listFinanceActionOptions}
              callback={executeListAction}
              callbackParams={{}}
            />)
        },
        isFreeAction: true,
      },

  ] : [
    {
      hidden: false,
      disabled: false,
      icon: () => {
        return(
          <IconButtonMenu
            icon={<SaveAlt/>}
            size={"small"}
            offset={-1}
            style={{ color: theme.palette.primary.main }}
            tooltip={"Download Flight Schedule Reports"}
            menuItems={listActionOptions}
            callback={executeListAction}
            callbackParams={{}}
          />)
      },
      isFreeAction: true,
    },
    {
      hidden: false,
      disabled: false,
      icon: () => {
        return(
          <IconButtonMenu
            icon={<Email/>}
            size={"small"}
            offset={-1}
            style={{ color: theme.palette.primary.main }}
            tooltip={"Email Flight Schedule Reports"}
            menuItems={sendActionOptions}
            callback={executeEmailAction}
            callbackParams={{}}
          />)
      },
      isFreeAction: true,
    },
    {
      hidden: !state.canEdit || props.showReview,
      disabled: moment.utc({year, month: (month), day: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0}).isBefore(moment.utc()),
      icon: 'add_box',
      tooltip: "Add Trip",
      isFreeAction: true,
      onClick: ()=>{openAddTrip(__.cloneDeep({...newTripRecordTemplate, tripDate: moment.utc({year, month: (month - 1), day: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0}).toISOString()}))}
    },
    {
      hidden: !props.showReview,
      icon: 'refresh',
      tooltip: "Refresh",
      isFreeAction: true,
      onClick: ()=>{refreshList()}
    },
    (rowData) => (
    {
      hidden: false,
      disabled: false,
      icon: () => {
        const  flags = rowData.flags ? rowData.flags : {};
        const rowActions = [...getRowActionOptions(rowData)];
        if ((flags['trip_folders_created'])) {
          __.remove(rowActions, function(item) {
            return (item.action === "Create Trip Folders");
          });
        }
        if ( !(flags['eurocargo_draft_reconciliation_downloaded'])) {
          __.remove(rowActions, function(item) {
            return (item.action === "Confirm EuroCargo Draft Reconciliation");
          });
        }
        if ((flags['confirm_eurocargo_draft_reconciliation'])) {
          __.remove(rowActions, function(item) {
            return (item.action === "Confirm EuroCargo Draft Reconciliation" || item.action === "EuroCargo Draft Reconciliation");
          });
        } else {
          __.remove(rowActions, function(item) {
            return (item.action === "EuroCargo Reconciliation");
          });
        }
        return(
        <IconButtonMenu
          icon={<PlaylistAddCheck/>}
          size={"small"}
          offset={-1}
          style={{ color: theme.palette.primary.main }}
          tooltip={"Trip Actions and Reports"}
          menuItems={rowActions}
          callback={executeRowAction}
          callbackParams={{rowData: rowData}}
        />)
      },
      isFreeAction: false,
    }),
    (rowData) => ({
      hidden: !state.canEdit,
      disabled: (rowData.cancelled),
      icon: 'edit',
      tooltip: "Edit",
      isFreeAction: false,
      onClick: ()=>{
        handleEditTripAction(rowData.tripUuid, `${rowData.tripPrefix}-${rowData.tripNo}`);
        //openEditTrip(rowData)
      }
    }),
    (rowData) => (rowData.cancelled
      ?{
        hidden: !state.canEdit,
        disabled: (moment(rowData.tripDate).isBefore(moment().subtract(7,'days'))),
        icon: 'airplanemode_inactive',
        tooltip: "Restore Trip",
        isFreeAction: false,
        onClick: toggleCancelled
      }
      :{
        hidden: !state.canEdit,
        disabled: (moment(rowData.tripDate).isBefore(moment().subtract(7,'days'))),
        icon: 'airplanemode_inactive',
        tooltip: "Cancel Trip",
        isFreeAction: false,
        onClick: toggleCancelled
      }
    ),
  ];

  if (props.showReview && !props.financeView) {
    rowActions.push(
      (rowData) => {
        return ({
          icon: "redo",
          tooltip: "Ignore change.",
          isFreeAction: false,
          onClick: handleIgnoreChangesClick,
          //hidden: !props.showReview || ['ORIGIN', 'DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData.reasonCode}`) > 0,
          disabled: !state.canEdit,
        })
      },
    )
  }

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          rowStyle: (rowData)=> ({opacity: rowData.cancelled?0.5:1}),
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          //paging: false
          //paginationType: "stepped"
        }}
        actions={[...rowActions

        ]}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[15, 30, 45]}
            />
          ),
          Toolbar: (toolbarProps) => {
            return (
              <div style={{ position: "relative" }}>
                <MTableToolbar {...toolbarProps} />
                {props.showReview && !props.financeView?
                  <>
                    <div style={{position: "absolute", left: 120, top: 16}}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>Hide Future Trips</Typography>
                        <Switch checked={props.hideFutureReviewItems} onChange={()=>{eventBus.trigger("FlightList.toggleHideFutureTrip");}}/>
                      </Stack>
                    </div>
                    <div style={{position: "absolute", left: 330, top: 16}}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>Operational Tasks</Typography>
                        <Switch checked={props.showOperationsReviewItems} onChange={()=>{eventBus.trigger("FlightList.toggleShowOperationsTasks");}}/>
                      </Stack>
                    </div>
                  </>
                  : props.showReview && props.financeView?
                    <div style={{position: "absolute", left: 120, top: 16}}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>Hide Future Trips</Typography>
                        <Switch checked={props.hideFutureReviewItems} onChange={()=>{eventBus.trigger("FlightList.toggleHideFutureTrip");}}/>
                      </Stack>
                    </div>
                  : null
                }
              </div>
            );
          },
        }}
        title="Trips"
        columns={props.financeView && !props.showReview ? getFinanceTripColumns({canEdit: state.canEdit}) : getTripColumns({canEdit: state.canEdit})}
        detailPanel={
          state.monthTabIndex === 1 || props.financeView
            ? undefined
            :
              [
                {
                  tooltip: "Show Trip Sectors",
                  icon: 'flight',
                  render: (rowData) => {
                    return (
                      <div >
                        <div style={{ backgroundColor: "#f2f2f2" }}>
                          <TripLegList
                            canEdit={state.canEdit}
                            tripUuid={rowData.tripUuid}
                            tripPrefix={rowData.tripPrefix}
                            tripNo={rowData.tripNo}
                            classes={classes}
                          />
                        </div>
                      </div>
                    );
                  },
                },
                {
                  tooltip: "Show Trip Cargo Details",
                  icon: ()=>(<AirCargoIcon/>),
                  openIcon: ()=>(<AirCargoInverseIcon/>),
                  render: (rowData) => {
                    const legs = rowData.legs;
                    const onwardDestinations = [];
                    for (let i = 0; i < legs.length; i++) {
                      if (legs[i].transportType !== "PRIMARY") {
                        onwardDestinations.push(legs[i].destinationAirportCode);
                      }
                    }
                    return (
                      <div>
                        <div style={{ backgroundColor: "#f2f2f2" }}>
                          <TripClients
                            canEdit={state.canEdit}
                            tripUuid={rowData.tripUuid}
                            tripRoute={buildPrimaryRouteFromLegs(rowData.legs)}
                            onwardDestinations={onwardDestinations}
                          />
                        </div>
                      </div>
                    );
                  },
                },
                {
                  tooltip: "Show Trip ULD Replenishment",
                  icon: ()=>(<UldContainerClosedIcon/>),
                  openIcon: ()=>(<UldContainerIcon/>),
                  render: (rowData) => {
                    return (
                      <div>
                        <div style={{ backgroundColor: "#f2f2f2" }}>
                          <TripUldStacks
                            canEdit={state.canEdit}
                            tripUuid={rowData.tripUuid}
                            tripRoute={rowData.tripRoute}
                          />
                        </div>
                      </div>
                    );
                  },
                },
                {
                  tooltip: "Show Trip Messaging",
                  icon: "notifications",
                  openIcon: "notifications_none",
                  render: (rowData) => {
                    return (
                      <div>
                        <div style={{ backgroundColor: "#f2f2f2" }}>
                          <TripMessaging
                            canEdit={state.canEdit}
                            listVariant={"TRIP"}
                            tripUuid={rowData.tripUuid}
                            route={rowData.tripRoute}
                          />
                        </div>
                      </div>
                    );
                  },
                },
              ]
        }
        data={props.showOperationsReviewItems ? __.filter(trips.data, task => ['CLIENT-ROUTE-MISMATCH', 'ULD-STACK-ROUTE-MISMATCH'].indexOf(task.reasonCode) > -1) : trips.data}
      />
      <TripCosts
        open={actualCosts.open}
        trip={actualCosts.trip}
        tripData={actualCosts.tripData}
        onChange={(tripActualCosts) => {
          updateActualCosts(tripActualCosts);
        }}
      >
      </TripCosts>
      <TripConfirmation
        open={tripConfirmation.open}
        trip={tripConfirmation.trip}
        object={tripConfirmation.action}
        requiredConfirmation={tripConfirmation.requiredConfirmation}
        user={tripConfirmation.user}
        onChange={(confirmation) => {
          updateConfirmation(confirmation);
        }}
      >
      </TripConfirmation>

      {reportCriteria.open &&
        <TripReportCriteriaDialog
          open={reportCriteria.open}
          title={reportCriteria.title}
          selectionFields={reportCriteria.selectionFields}
          defaultStartDate={reportCriteria.fromDate}
          defaultEndDate={reportCriteria.toDate}
          defaultCompany={reportCriteria.company}
          defaultAirport={reportCriteria.airport}
          defaultAirline={reportCriteria.airline}
          defaultScratchPadUser={reportCriteria.scratchPadUser}
          defaultScratchPad={reportCriteria.scratchPad}
          onChange={(criteria) => {
            if (reportCriteria.onChange) reportCriteria.onChange(criteria);
          }}
        />
      }

      <AirportSelectionDialog
        open={selectAirport.open}
        withSupplier={selectAirport.mode === "email"}
        onChange={(airport, companyUuid, excludeAcmi) => {
          if (airport && companyUuid) {
            downloadFlightSchedule("airport", airport, selectAirport.mode === "download", companyUuid, null, excludeAcmi);
          } else if (airport) {
            downloadFlightSchedule("airport", airport, selectAirport.mode === "download", null, null, excludeAcmi);
          }
          setSelectAirport({...selectAirport, open: false, airport: airport})
        }}
      >
      </AirportSelectionDialog>
      <CustomerSelectionDialog
        open={selectCustomer.open}
        airline={selectCustomer.airline}
        onChange={(customerUuid, companyName, airlineCode) => {
          if (selectCustomer.airline && customerUuid) {
            downloadFlightSchedule("airline", airlineCode, selectCustomer.mode === "download");
          }
          else if (customerUuid) {
            downloadFlightSchedule("customer", customerUuid, selectCustomer.mode === "download", customerUuid, companyName);
          }
          setSelectCustomer({...selectCustomer, open: false, customerUuid: null, airline: null})
        }}
      />

      <AddTripDialog classes={classes}/>
      <EditTripDialog
        systemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={extraKeys}
        handleUpdate={handleUpdateTrip}
        formatChars={formatChars}
        classes={classes}
        canEdit={state.canEdit}
        ownerDepartment={defaultValues?.tripOwnerDepartment || "COMMERCIAL"}
      />

      <ViewCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={costingDefaultValues}
        classes={costingClasses}
      />

      <EditCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={costingDefaultValues}
        classes={costingClasses}
        handleUpdate={handleUpdateCosting}
      />

      <FinanceTripLegsDialog
        systemData={SystemData}
        classes={costingClasses}
      />

      <ViewFuelAnnexDialog
        systemData={SystemData}
        classes={costingClasses}
      />

      <TripContractInvoiceDialog
        systemData={SystemData}
        classes={classes}
      />

      {viewDocuments.open
        ?
          <DocumentViewer
            dialogStates={
              {showDialog: viewDocuments.open,
                title: `Contract Viewer`,
                record: viewDocuments.documentsToView[0]
              }
            }
            close={()=>{setViewDocuments({...viewDocuments, open: false})}}
            documents={viewDocuments.documentsToView}
          />
        :
          <></>
      }

      {/* {dialogBoxes} */}
    </div>
  );

}

export default TripList;
